<template>
  <div>
    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>
      <b-card-body ref="filters">
        <b-row>
          <b-col cols="12" md="8" class="mb-1">
            <b-form-group label="Período" label-for="searchRangeDate">
              <flat-pickr
                id="searchRangeDate"
                v-model="rangeDate"
                class="form-control"
                :config="flatPickrConfig"
                :placeholder="`01 Jan 2020 até 31 Dez ${actualYear}`"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <b-form-group label="Solução" label-for="searchSolucition">
              <all-select
                id="searchSolucition"
                v-model="solution"
                :options="[
                  { id: 1, solution: 'Entrevista Paga' },
                  { id: 2, solution: 'Assinatura' },
                  { id: 3, solution: 'Solução financeira' }
                ]"
                label="solution"
                take-this="solution"
                track-by="solution"
                multiple
                :close-on-select="false"
                :loading="loading.solutions"
              >
              </all-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" class="mb-md-0 mb-2 text-right">
            <b-button
              variant="outline-primary"
              @click.prevent="search()"
              :disabled="loading.search"
            >
              <b-spinner v-if="loading.search" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>
                {{ loading.search ? "Aguarde..." : "Pesquisar" }}
              </span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options="{ search, searching, loading }">
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <!-- Search -->
          <b-col cols="12" md="6">
            <b-row class="d-flex align-items-center justify-content-end">
              <div class="mx-1" cols="12" md="6">
                <b-button
                  variant="outline-primary"
                  :disabled="loading.export"
                  @click.prevent="exportData()"
                >
                  <b-spinner v-if="loading.export" small />
                  <feather-icon
                    v-else
                    icon="ArrowDownCircleIcon"
                    class="mr-50"
                  />
                  <span>
                    {{ loading.export ? "Aguarde..." : "Exportar planilha" }}
                  </span>
                </b-button>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="cod_contrato"
        show-empty
        empty-text="Não foram encontrados contratos para esta busca"
        :busy="loading.search"
        :no-local-sorting="true"
        :sort-by="sortField"
        @sort-changed="sort"
        @row-clicked="goToContractDetails"
      >
        <!-- Column: Código do Contrato -->
        <template #cell(cod_contrato)="data">
          <div class="text-wrap text-center">
            <span class="align-text-top">{{ data.item.cod_contrato }}</span>
          </div>
        </template>
        <!-- Column: Data de Geração do Contrato -->
        <template #cell(contrato_data_geracao)="data">
          <div class="text-wrap text-center">
            <span class="align-text-top">{{ data.item.contrato_data_geracao }}</span>
          </div>
        </template>
        <!-- Column: Status do Contrato -->
        <template #cell(contrato_status)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.contrato_status }}</span>
          </div>
        </template>
        <!-- Column: Franquia do Consultor -->
        <template #cell(consultor_nome_franquia)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.consultor_nome_franquia }}</span>
          </div>
        </template>
        <!-- Column: Consultor Responsável -->
        <template #cell(consultor_dono_contrato)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.consultor_dono_contrato }}</span>
          </div>
        </template>
        <!-- Column: Nome do Cliente -->
        <template #cell(cliente_nome)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.cliente_nome }}</span>
          </div>
        </template>
        <!-- Column: Produto -->
        <template #cell(produto)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.produto }}</span>
          </div>
        </template>
        <!-- Column: Valor do Contrato -->
        <template #cell(contrato_valor)="data">
          <div class="text-wrap text-center">
            <span class="align-text-top">{{ data.item.contrato_valor }}</span>
          </div>
        </template>
        <!-- Column: Quem Aprovou o Pagamento -->
        <template #cell(pagamento_quem_aprovou)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.pagamento_quem_aprovou }}</span>
          </div>
        </template>
        <!-- Column: Valor de Repasse Futuro -->
        <template #cell(pagamento_valor_repasse_futuro)="data">
          <div class="text-wrap text-center">
            <span class="align-text-top">{{ data.item.pagamento_valor_repasse_futuro }}</span>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BFormGroup,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import UserDetailPopover from "@/modules/shared/components/UserDetailPopover";
import AllSelect from "@/modules/shared/components/AllSelect";
import ShortcutButtonsPlugin from "shortcut-buttons-flatpickr";
import todayButton from "@/helpers/todayButton";
import flatPickr from "vue-flatpickr-component";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import { getRangeDates } from "@/helpers/date_picker";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BFormGroup,
    vSelect,
    UserDetailPopover,
    AllSelect,
    flatPickr,
  },
  setup() {
    const actualYear = new Date().getFullYear()
    return { toast: useToast(), actualYear };
  },
  data() {
    return {
      loading: {
        search: false,
        export: false,
      },
      flatPickrConfig: {
        altFormat: "j M Y",
        altInput: true,
        mode: "range",
        dateFormat: "Y-m-d",
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      tableColumns: [
        { key: "cod_contrato", label: "Código do Contrato", sortable: true },
        { key: "contrato_data_geracao", label: "Data de Geração do Contrato", sortable: true },
        { key: "contrato_status", label: "Status do Contrato", sortable: true },
        { key: "consultor_nome_franquia", label: "Franquia do Consultor", sortable: true },
        { key: "consultor_dono_contrato", label: "Consultor Responsável", sortable: true },
        { key: "cliente_nome", label: "Nome do Cliente", sortable: true },
        { key: "produto", label: "Produto", sortable: true },
        { key: "contrato_valor", label: "Valor do Contrato", sortable: true },
        { key: "pagamento_quem_aprovou", label: "Quem Aprovou o Pagamento", sortable: true },
        { key: "pagamento_valor_repasse_futuro", label: "Valor de Repasse Futuro", sortable: true },
      ],
      order: undefined,
      sortField: null,
      solution: [],
      rangeDate: undefined,
      currentPage: 1,
      itemsPerPage: 1,
    };
  },
  computed: {
    ...mapGetters({
      contracts: types.EPS_SUBSCRIPTIONS_AND_SOLUTIONS,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items: function () {
      return this.contracts?.data || [];
    },
    totalItems: function () {
      return this.contracts?.total || 0;
    },
    pageItemsFrom: function () {
      return this.contracts?.from || 0;
    },
    pageItemsTo: function () {
      return this.contracts?.to || 0;
    },
  },
  mounted() {
    const MONTHS_GAP = 1;
    let initialDate = new Date();
    initialDate.setMonth(initialDate.getMonth() - MONTHS_GAP);
    let endDate = new Date();
    endDate.setMonth(endDate.getMonth() + MONTHS_GAP);
    this.rangeDate = `${initialDate.getFullYear()}-${initialDate.getMonth() + 1}-${initialDate.getDate()} até ${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`;
    this.itemsPerPage = this.initialItemsPerPage;
    if (this.$route.query.startDate && this.$route.query.endDate) {
      this.rangeDate = this.$route.query.startDate + " até " + this.$route.query.endDate;
      this.search(1);
    }
  },
  methods: {
    ...mapActions({
      searchContracts: types.SEARCH_EPS_SUBSCRIPTIONS_AND_SOLUTIONS,
      exportContracts: types.EXPORT_EPS_SUBSCRIPTIONS_AND_SOLUTIONS,
    }),
    goToContractDetails(row, index) {
      this.$router.push({
        name: "contract-details-summary",
        params: { id: row.cod_contrato },
      });
    },
    search(currentPage) {
      this.loading.search = true;
      this.currentPage = currentPage;
      const range_date = getRangeDates(this.rangeDate);

      this.searchContracts({
        initialDate: range_date.start,
        endDate: range_date.end,
        solution: this.solution,
        limit: this.itemsPerPage,
        currentPage: Number.isInteger(currentPage) ? currentPage : 1,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os contratos. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.search = false;
        });
    },
    sort(event) {
      //clear in third click
      if (!event.sortDesc && this.sortField == event.sortBy) {
        this.order = undefined;
        this.sortField = null;
      } else {
        var direction = event.sortDesc ? " DESC" : " ASC";
        this.order = event.sortBy + direction;
        this.sortField = event.sortBy;
      }
      this.search(1);
    },
    exportData() {
      this.loading.export = true;
      this.exportContracts({
        order: this.order,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os contratos. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.export = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
